import * as React from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box, Drawer, List, Toolbar } from "@mui/material";
import { useLocation } from "react-router-dom";
import { MINI_DRAWER_WIDTH } from "./constants";
import SidebarDividerItem from "./sidebar-divider-item";
import { getDrawerSxTransitionMixin, getDrawerSharedSx } from "./mixins";
import sidebarElements from "./sidebar-elements";
import useCanShowItem from "./use-can-show-item";
import SidebarRenderPageItem from "./sidebar-page-item-wrapper";

export interface SidebarProps {
  expanded?: boolean;
  disableCollapsibleSidebar?: boolean;
}

const Sidebar = ({
  expanded = true,
  disableCollapsibleSidebar = false,
}: SidebarProps) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const canShowItem = useCanShowItem();

  const isOverSmViewport = useMediaQuery(theme.breakpoints.up("sm"));
  const isOverMdViewport = useMediaQuery(theme.breakpoints.up("md"));

  const [isFullyExpanded, setIsFullyExpanded] = React.useState(expanded);
  const [expandedItemIds, setExpandedItemIds] = React.useState<string[]>([]);

  const handlePageItemClick = React.useCallback(
    (id: string, hasNested: boolean) => {
      if (!hasNested) return;

      setExpandedItemIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    },
    [],
  );

  React.useEffect(() => {
    if (expanded) {
      const timeout = setTimeout(
        () => setIsFullyExpanded(true),
        theme.transitions.duration.enteringScreen,
      );
      return () => clearTimeout(timeout);
    }
    setIsFullyExpanded(false);
    return undefined;
  }, [expanded, theme.transitions.duration.enteringScreen]);

  const mini = !disableCollapsibleSidebar && !expanded;
  const hasDrawerTransitions =
    isOverSmViewport && (!disableCollapsibleSidebar || isOverMdViewport);

  const getDrawerContent = React.useMemo(
    () => (
      <>
        <Toolbar />
        <Box
          data-testid="sidebar-content"
          component="nav"
          aria-label="Desktop"
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "auto",
            scrollbarGutter: mini ? "stable" : "auto",
            overflowX: "hidden",
            pt: mini ? 2 : 1,
            ...(hasDrawerTransitions
              ? getDrawerSxTransitionMixin(isFullyExpanded, "padding")
              : {}),
          }}
        >
          <List
            dense
            sx={{
              padding: mini ? 0 : 0.5,
              mb: 4,
              width: mini ? MINI_DRAWER_WIDTH : "auto",
            }}
          >
            {sidebarElements.mainItems
              .filter((item) =>
                canShowItem({
                  requiredPermission: item.requiredPermission,
                  ldFlag: item.ldFlag,
                }),
              )
              .map((item) => (
                <SidebarRenderPageItem
                  key={item.id}
                  item={item}
                  mini={mini}
                  isFullyExpanded={isFullyExpanded}
                  expanded={expandedItemIds.includes(item.id)}
                  pathname={pathname}
                  handlePageItemClick={handlePageItemClick}
                />
              ))}

            {sidebarElements.adminItems.length > 0 && <SidebarDividerItem />}

            {sidebarElements.adminItems
              .filter((item) =>
                canShowItem({
                  requiredPermission: item.requiredPermission,
                  ldFlag: item.ldFlag,
                }),
              )
              .map((item) => (
                <SidebarRenderPageItem
                  key={item.id}
                  item={item}
                  mini={mini}
                  isFullyExpanded={isFullyExpanded}
                  expanded={expandedItemIds.includes(item.id)}
                  pathname={pathname}
                  handlePageItemClick={handlePageItemClick}
                />
              ))}
          </List>
        </Box>
      </>
    ),
    [
      mini,
      hasDrawerTransitions,
      isFullyExpanded,
      expandedItemIds,
      pathname,
      canShowItem,
      handlePageItemClick,
    ],
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", md: "block" },
        ...getDrawerSharedSx({ mini, expanded }),
      }}
    >
      {getDrawerContent}
    </Drawer>
  );
};

export default Sidebar;
